import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'

import { ErrorHandler } from './ErrorHandler'
import { ErrorType } from './errorTypes'
import type { ApiClientConfig, ApiResponse, RequestOptions } from './types'

import { useAuthStore } from '@/stores/authStore'

export class ApiClient {
  private instance: AxiosInstance
  private config: ApiClientConfig
  private pendingRequests = new Map<string, Promise<unknown>>()

  constructor(config: ApiClientConfig) {
    this.config = this.mergeConfig(config)
    this.instance = this.createInstance()
    this.setupInterceptors()
  }

  // 统一配置合并逻辑
  private mergeConfig(config: ApiClientConfig): ApiClientConfig & {
    timeout: number
    withAuth: boolean
    mockEnabled: boolean
  } {
    return {
      timeout: 10000,
      withAuth: true,
      mockEnabled: false,
      ...config
    }
  }

  private createInstance(): AxiosInstance {
    const defaultHeaders = { 'Content-Type': 'application/json' }
    
    return axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        ...defaultHeaders,
        ...this.config.headers
      }
    })
  }

  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 添加认证头
        if (this.config.withAuth) {
          const { token } = useAuthStore.getState()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }
        // console.log(import.meta.env)
        console.log(`API请求: ${config.method?.toUpperCase()} ${config.url}`)
        return config
      },
      (error) => {
        console.error('请求拦截器错误:', error)
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(
          `API响应成功: ${response.config.url} 状态: ${response.status}`
        )
        return response
      },
      async (error: AxiosError<ApiResponse>) => {
        // 使用统一错误处理器
        const processedError = await ErrorHandler.handleError(error, {
          url: error.config?.url,
          method: error.config?.method
        })

        // 针对认证错误的特殊处理（保持原有的 token 刷新逻辑）
        if (processedError.type === ErrorType.AUTH && this.config.withAuth) {
          const retryResult = await this.handleUnauthorized(error)
          if (retryResult) {
            return retryResult
          }
        }

        // 抛出标准化的错误
        const enhancedError = new Error(processedError.userMessage) as Error & {
          processedError: typeof processedError
          originalError: typeof error
        }
        enhancedError.processedError = processedError
        enhancedError.originalError = error
        // console.log(enhancedError)
        return Promise.reject(enhancedError)
      }
    )
  }

  private async handleUnauthorized<T = unknown>(
    error: AxiosError
  ): Promise<T | undefined> {
    const { refreshToken, updateToken } = useAuthStore.getState()

    if (refreshToken) {
      try {
        // 尝试刷新token
        const refreshResponse = await axios.post(
          `${this.config.baseURL}/auth/refresh`,
          {
            refreshToken
          }
        )

        if (refreshResponse.data.success) {
          const { token: newToken, refreshToken: newRefreshToken } =
            refreshResponse.data.data
          updateToken(newToken, newRefreshToken)

          // 重新发送原请求
          if (error.config) {
            error.config.headers.Authorization = `Bearer ${newToken}`
            // return this.instance.request<T>(error.config);
            const retryResponse = await this.instance.request<T>(error.config)
            return retryResponse.data // 只返回 data
          }
        }
      } catch (refreshError) {
        console.error('刷新token失败:', refreshError)
        this.clearAuthAndRedirect()
      }
    } else {
      this.clearAuthAndRedirect()
    }
  }

  private clearAuthAndRedirect(): void {
    const { clearAuth } = useAuthStore.getState()
    clearAuth()

    // 清理相关localStorage
    localStorage.removeItem('access_token')
    localStorage.removeItem('dingtalk_token')
    localStorage.removeItem('user_info')
    localStorage.removeItem('dingtalk_user_info')

    window.location.href = '/auth'
  }


  private getRequestKey(url: string, options?: RequestOptions): string {
    return `${url}_${JSON.stringify(options?.params || {})}`
  }

  // 统一构建请求配置
  private buildRequestConfig(options?: RequestOptions) {
    return {
      params: options?.params,
      headers: options?.headers,
      timeout: options?.timeout,
      onUploadProgress: options?.onUploadProgress
    }
  }

  // 统一的请求执行器
  private async executeRequest<T>(
    requestFn: () => Promise<{ data: T }>,
    dedupeKey?: string
  ): Promise<T> {
    if (dedupeKey) {
      return this.dedupeRequest(async () => {
        const response = await requestFn()
        return response.data
      }, dedupeKey)
    }
    
    const response = await requestFn()
    return response.data
  }

  private async dedupeRequest<T>(
    requestFn: () => Promise<T>,
    key: string
  ): Promise<T> {
    const existingRequest = this.pendingRequests.get(key)
    if (existingRequest) {
      return existingRequest as Promise<T>
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key)
    })

    this.pendingRequests.set(key, promise)
    return promise
  }

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    const key = this.getRequestKey(url, options)
    return this.executeRequest(
      () => this.instance.get(url, this.buildRequestConfig(options)),
      key
    )
  }

  async post<T>(
    url: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.executeRequest(
      () => this.instance.post(url, data, this.buildRequestConfig(options))
    )
  }

  async put<T>(
    url: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.executeRequest(
      () => this.instance.put(url, data, this.buildRequestConfig(options))
    )
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.executeRequest(
      () => this.instance.delete(url, this.buildRequestConfig(options))
    )
  }

  async upload<T>(
    url: string,
    file: File,
    options?: RequestOptions
  ): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const uploadConfig = {
      ...this.buildRequestConfig(options),
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options?.headers
      },
      timeout: options?.timeout || 30000
    }

    return this.executeRequest(
      () => this.instance.post(url, formData, uploadConfig)
    )
  }

  async download(url: string, options?: RequestOptions): Promise<Blob> {
    const downloadConfig = {
      ...this.buildRequestConfig(options),
      timeout: options?.timeout || 30000,
      responseType: 'blob' as const
    }

    return this.executeRequest(
      () => this.instance.get(url, downloadConfig)
    )
  }

  // 获取原始axios实例，用于特殊需求
  getInstance(): AxiosInstance {
    return this.instance
  }

  // 清理待处理请求
  clearPendingRequests(): void {
    this.pendingRequests.clear()
  }

  // 配置错误处理器
  configureErrorHandler(config: Partial<import('./errorTypes').ErrorHandlerConfig>): void {
    ErrorHandler.configure(config)
  }

  // 带重试的请求方法
  async requestWithRetry<T>(
    requestFn: () => Promise<T>,
    retryOptions?: { maxRetries: number; delay: number }
  ): Promise<T> {
    return ErrorHandler.retryRequest(requestFn, retryOptions)
  }
}
